import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, Footer, SEOHead } from '../components';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { 
  ChartBarIcon,
  MapPinIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  UserGroupIcon,
  MegaphoneIcon,
  MagnifyingGlassIcon,
  ShoppingBagIcon,
  ArrowRightIcon,
  CheckIcon,
  StarIcon,
  BuildingOfficeIcon,
  HomeIcon,
  BeakerIcon,
  TruckIcon,
  ScaleIcon,
  BuildingStorefrontIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

const CaseStudiesPage: React.FC = () => {
  const navigate = useNavigate();
  useSEO(pageConfigs.caseStudies);
  const [activeFilter, setActiveFilter] = useState('all');

  const handleScheduleConsultation = () => {
    navigate('/consultation');
  };

  const handleViewServices = () => {
    navigate('/');
  };

  const handleViewProject = () => {
    navigate('/portfolio');
  };

  const handleGetStarted = () => {
    navigate('/quote');
  };

  const handleLearnMore = () => {
    navigate('/about');
  };

  const caseStudies = [
    {
      title: "4.8-Star Rated Mobile App with 1M+ Downloads",
      category: "mobile-App",
      industry: "Dating App",
      icon: <DevicePhoneMobileIcon className="w-8 h-8" />,
      image: "",
      challenge: "A dating app targeting the middle east users.",
      solution: "As an affiliate to the app install goal, we drived organic app installs for over a period of 6 months.",
      results: [
        "10K+ downloads across iOS and Android",
        "4.8-star average rating",
        "65% increase in customer engagement",
        "120% increase in mobile transactions"
      ],
      testimonial: {
        quote: "The mobile app has performed very well and we have see quite large amount of installs after the campaign was launched.",
        author: "Madalina"
      }
    },
    {
      title: "3.2x ROAS Improvement for Beauty Brand",
      category: "meta-ads",
      industry: "e-commerce",
      icon: <MegaphoneIcon className="w-8 h-8" />,
      image: "🛒",
      challenge: "An e-commerce beauty brand was experiencing high ad costs and low return on ad spend on Facebook and Instagram.",
      solution: "Implemented strategic audience targeting, creative testing, and campaign optimization for Meta advertising campaigns.",
      results: [
        "3.2x improvement in ROAS",
        "45% reduction in cost per acquisition",
        "185% increase in conversion rate",
        "250% increase in revenue from Meta ads"
      ],
      testimonial: {
        quote: "The transformation in our Meta advertising performance has been remarkable. Our ad spend is now much more efficient, and we're seeing significantly better results.",
        author: "Nitin Gupta",
        title: "Marketing Director, Esminis Essentials LLP"
      }
    },
    {
      title: "200% Increase in Amazon Sales for kids gummies Brand",
      category: "amazon-ads",
      industry: "technology",
      icon: <ShoppingBagIcon className="w-8 h-8" />,
      image: "💻",
      challenge: "A wellness and kids gummies brand had low visibility and sales on Amazon despite quality products.",
      solution: "Implemented comprehensive Amazon advertising strategy and product listing optimization.",
      results: [
        "500% increase in Amazon sales",
        "320% improvement in ROAS",
        "75% reduction in ACoS",
        "Top 3 ranking in all target categories"
      ],
      testimonial: {
        quote: "Our Amazon business has been completely transformed. The strategic approach to advertising and product optimization has resulted in exponential growth in our sales.",
        author: "Nitin Gupta",
        title: "Founder, Esminis Essentials LLP"
      }
    }
  ];

  const filteredCaseStudies = activeFilter === 'all' 
    ? caseStudies 
    : caseStudies.filter(study => study.category === activeFilter || study.industry === activeFilter);

  const categoryIcons: Record<string, React.ReactNode> = {
    'lead-generation': <MapPinIcon className="w-5 h-5" />,
    'mobile-development': <DevicePhoneMobileIcon className="w-5 h-5" />,
    'website-development': <ComputerDesktopIcon className="w-5 h-5" />,
    'digital-marketing': <ChartBarIcon className="w-5 h-5" />,
    'social-media': <UserGroupIcon className="w-5 h-5" />,
    'meta-ads': <MegaphoneIcon className="w-5 h-5" />,
    'google-ads': <MagnifyingGlassIcon className="w-5 h-5" />,
    'amazon-ads': <ShoppingBagIcon className="w-5 h-5" />,
    'healthcare': <BeakerIcon className="w-5 h-5" />,
    'financial-services': <BuildingOfficeIcon className="w-5 h-5" />,
    'e-commerce': <ShoppingBagIcon className="w-5 h-5" />,
    'real-estate': <HomeIcon className="w-5 h-5" />,
    'technology': <BeakerIcon className="w-5 h-5" />,
    'manufacturing': <TruckIcon className="w-5 h-5" />,
    'professional-services': <ScaleIcon className="w-5 h-5" />,
    'hospitality': <BuildingStorefrontIcon className="w-5 h-5" />,
    'home-services': <HomeIcon className="w-5 h-5" />
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.caseStudies} />
      <Header />

      {/* Hero Section */}
      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6 leading-tight">
              Our Client 
              <span className="text-blue-600"> Success Stories</span>
            </h1>
            
            <p className="text-xl text-slate-700 mb-8 leading-relaxed">
              Explore how we've helped businesses across industries achieve exceptional results through strategic digital marketing and technology solutions.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <button 
                onClick={handleScheduleConsultation}
                className="bg-slate-900 hover:bg-slate-800 text-white font-semibold py-4 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                <span>Schedule Consultation</span>
                <ArrowRightIcon className="w-5 h-5" />
              </button>
              <button 
                onClick={handleViewServices}
                className="bg-white hover:bg-gray-50 text-slate-900 font-semibold py-4 px-8 rounded-lg border-2 border-slate-200 transition-colors"
              >
                View Services
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <h2 className="text-xl font-display font-bold text-slate-900 mb-4 md:mb-0">
              Filter Case Studies:
            </h2>
            
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => setActiveFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  activeFilter === 'all' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                All
              </button>
              
              <button 
                onClick={() => setActiveFilter('lead-generation')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center space-x-1 ${
                  activeFilter === 'lead-generation' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <MapPinIcon className="w-4 h-4" />
                <span>Lead Generation</span>
              </button>
              
              <button 
                onClick={() => setActiveFilter('mobile-development')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center space-x-1 ${
                  activeFilter === 'mobile-development' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <DevicePhoneMobileIcon className="w-4 h-4" />
                <span>Mobile Development</span>
              </button>
              
              <button 
                onClick={() => setActiveFilter('website-development')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center space-x-1 ${
                  activeFilter === 'website-development' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <ComputerDesktopIcon className="w-4 h-4" />
                <span>Website Development</span>
              </button>
              
              <button 
                onClick={() => setActiveFilter('digital-marketing')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center space-x-1 ${
                  activeFilter === 'digital-marketing' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <ChartBarIcon className="w-4 h-4" />
                <span>Digital Marketing</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8">
            {filteredCaseStudies.map((study, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-slate-100">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <div className="text-blue-600">{study.icon}</div>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="text-sm font-semibold bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center space-x-1">
                          {categoryIcons[study.category]}
                          <span>{study.category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</span>
                        </div>
                        <div className="text-sm font-semibold bg-slate-200 text-slate-800 px-3 py-1 rounded-full flex items-center space-x-1">
                          {categoryIcons[study.industry]}
                          <span>{study.industry.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</span>
                        </div>
                      </div>
                      <h3 className="text-xl font-display font-bold text-slate-900">{study.title}</h3>
                    </div>
                  </div>
                  <div className="text-4xl">{study.image}</div>
                </div>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Challenge:</h4>
                    <p className="text-slate-600">{study.challenge}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Solution:</h4>
                    <p className="text-slate-600">{study.solution}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Results:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {study.results.map((result, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <CheckIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span className="text-sm text-slate-700">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg border border-slate-200">
                  <p className="text-slate-700 italic mb-4">"{study.testimonial.quote}"</p>
                  <div className="flex items-center space-x-2">
                    <div className="text-amber-400 text-sm">★★★★★</div>
                    <div>
                      <div className="font-semibold text-slate-900">{study.testimonial.author}</div>
                      <div className="text-sm text-slate-600">{study.testimonial.title}</div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <button 
                    onClick={handleViewProject}
                    className="w-full bg-white hover:bg-slate-100 text-slate-900 font-semibold py-3 px-6 rounded-lg border border-slate-200 transition-colors flex items-center justify-center space-x-2"
                  >
                    <span>View Full Case Study</span>
                    <ArrowRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl lg:text-3xl font-display font-bold mb-6">
              Ready to Become Our Next Success Story?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Schedule a consultation with our experts to discuss how we can help your business achieve exceptional results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleScheduleConsultation}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors"
              >
                Schedule Consultation
              </button>
              <button 
                onClick={handleViewServices}
                className="bg-transparent hover:bg-white/10 text-white font-semibold py-4 px-8 rounded-lg border-2 border-white/20 transition-colors"
              >
                View Services
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseStudiesPage;